import { InputField, SelectField } from '../../UI/FormFields';
import { formatCPF } from '../../../utils/cpfUtils';

interface Step1Data {
  nomeCompleto: string;
  nomeSocial: string;
  cpf: string;
  genero: string;
  etnia: string;
  dataNascimento: string;
  paisNascimento: string;
  ufNascimento: string;
  municipioNascimento: string;
  rg: string;
  orgaoEmissor: string;
  ufOrgaoEmissor: string;
  email: string;
  telefoneCelular: string;
  enderecoResidencial: string;
  cep: string;
  bairro: string;
  pais: string;
  municipio: string;
  uf: string;
  vinculoEspecifico: string;
}

interface Step1Props {
  data: Step1Data;
  errors: Partial<Record<keyof Step1Data, string>>;
  onChange: (name: keyof Step1Data, value: string) => void;
}

export default function Step1InformacoesPessoais({ data, errors, onChange }: Step1Props) {
  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    onChange('cpf', formatted);
  };

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    value = value.replace(/^(\d{5})(\d)/, '$1-$2');
    onChange('cep', value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
    value = value.replace(/(\d)(\d{4})$/, '$1-$2');
    onChange('telefoneCelular', value);
  };

  const generoOptions = [
    { value: 'masculino', label: 'Masculino' },
    { value: 'feminino', label: 'Feminino' },
    { value: 'outro', label: 'Outro' },
    { value: 'prefiro_nao_dizer', label: 'Prefiro não dizer' },
  ];

  const etniaOptions = [
    { value: 'branca', label: 'Branca' },
    { value: 'preta', label: 'Preta' },
    { value: 'parda', label: 'Parda' },
    { value: 'amarela', label: 'Amarela' },
    { value: 'indigena', label: 'Indígena' },
  ];

  const ufsOptions = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];

  return (
    <div className='space-y-8 animate-fadeIn'>
      <div className='bg-gray-50 p-6 mb-8 rounded-lg shadow-sm'>
        <h3 className='text-xl font-bold text-gray-900 mb-2 uppercase tracking-wide'>Dados Pessoais</h3>
        <p className='text-sm text-gray-600'>
          Preencha suas informações pessoais com atenção. Campos marcados com <span className="text-red-600 font-semibold">*</span> são obrigatórios.
        </p>
      </div>

      {/* Grid Responsivo */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5'>
        <div className='md:col-span-2'>
          <InputField
            label='Nome Completo'
            name='nomeCompleto'
            value={data.nomeCompleto}
            onChange={(e) => onChange('nomeCompleto', e.target.value)}
            required
            error={errors.nomeCompleto}
            placeholder='Digite seu nome completo'
          />
        </div>

        <div className='md:col-span-2'>
          <InputField
            label='Nome Social'
            name='nomeSocial'
            value={data.nomeSocial}
            onChange={(e) => onChange('nomeSocial', e.target.value)}
            placeholder='Nome pelo qual deseja ser chamado (opcional)'
          />
        </div>

        <InputField
          label='CPF'
          name='cpf'
          value={data.cpf}
          onChange={handleCPFChange}
          required
          error={errors.cpf}
          placeholder='000.000.000-00'
          maxLength={14}
        />

        <SelectField
          label='Gênero'
          name='genero'
          value={data.genero}
          onChange={(e) => onChange('genero', e.target.value)}
          options={generoOptions}
          required
          error={errors.genero}
        />

        <SelectField
          label='Etnia'
          name='etnia'
          value={data.etnia}
          onChange={(e) => onChange('etnia', e.target.value)}
          options={etniaOptions}
          required
          error={errors.etnia}
        />

        <InputField
          label='Data de Nascimento'
          name='dataNascimento'
          type='date'
          value={data.dataNascimento}
          onChange={(e) => onChange('dataNascimento', e.target.value)}
          required
          error={errors.dataNascimento}
        />

        <InputField
          label='País (nascimento)'
          name='paisNascimento'
          value={data.paisNascimento}
          onChange={(e) => onChange('paisNascimento', e.target.value)}
          required
          error={errors.paisNascimento}
          placeholder='Brasil'
        />

        <SelectField
          label='UF (nascimento)'
          name='ufNascimento'
          value={data.ufNascimento}
          onChange={(e) => onChange('ufNascimento', e.target.value)}
          options={ufsOptions}
          required
          error={errors.ufNascimento}
        />

        <div className='md:col-span-2'>
          <InputField
            label='Município (nascimento)'
            name='municipioNascimento'
            value={data.municipioNascimento}
            onChange={(e) => onChange('municipioNascimento', e.target.value)}
            required
            error={errors.municipioNascimento}
            placeholder='Nome do município'
          />
        </div>
      </div>

      {/* Seção RG */}
      <div className='pt-6 mt-6'>
        <h4 className='text-md font-semibold text-gray-800 mb-4'>Documento de Identidade</h4>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <InputField
            label='RG'
            name='rg'
            value={data.rg}
            onChange={(e) => onChange('rg', e.target.value)}
            required
            error={errors.rg}
            placeholder='00.000.000-0'
          />

          <InputField
            label='Órgão Emissor'
            name='orgaoEmissor'
            value={data.orgaoEmissor}
            onChange={(e) => onChange('orgaoEmissor', e.target.value)}
            required
            error={errors.orgaoEmissor}
            placeholder='SSP, Detran, etc.'
          />

          <SelectField
            label='UF do Órgão Emissor'
            name='ufOrgaoEmissor'
            value={data.ufOrgaoEmissor}
            onChange={(e) => onChange('ufOrgaoEmissor', e.target.value)}
            options={ufsOptions}
            required
            error={errors.ufOrgaoEmissor}
          />
        </div>
      </div>

      {/* Seção Contato */}
      <div className='pt-6 mt-6'>
        <h4 className='text-md font-semibold text-gray-800 mb-4'>Informações de Contato</h4>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <InputField
            label='E-mail'
            name='email'
            type='email'
            value={data.email}
            onChange={(e) => onChange('email', e.target.value)}
            required
            error={errors.email}
            placeholder='seuemail@exemplo.com'
          />

          <InputField
            label='Telefone Celular'
            name='telefoneCelular'
            type='tel'
            value={data.telefoneCelular}
            onChange={handlePhoneChange}
            required
            error={errors.telefoneCelular}
            placeholder='(00) 00000-0000'
            maxLength={15}
          />
        </div>
      </div>

      {/* Seção Endereço */}
      <div className='0  pt-6 mt-6'>
        <h4 className='text-md font-semibold text-gray-800 mb-4'>Endereço Residencial</h4>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div className='md:col-span-2'>
            <InputField
              label='Endereço Residencial'
              name='enderecoResidencial'
              value={data.enderecoResidencial}
              onChange={(e) => onChange('enderecoResidencial', e.target.value)}
              required
              error={errors.enderecoResidencial}
              placeholder='Rua, Avenida, número e complemento'
            />
          </div>

          <InputField
            label='CEP'
            name='cep'
            value={data.cep}
            onChange={handleCEPChange}
            required
            error={errors.cep}
            placeholder='00000-000'
            maxLength={9}
          />

          <InputField
            label='Bairro'
            name='bairro'
            value={data.bairro}
            onChange={(e) => onChange('bairro', e.target.value)}
            required
            error={errors.bairro}
            placeholder='Nome do bairro'
          />

          <InputField
            label='País'
            name='pais'
            value={data.pais}
            onChange={(e) => onChange('pais', e.target.value)}
            required
            error={errors.pais}
            placeholder='Brasil'
          />

          <SelectField
            label='UF'
            name='uf'
            value={data.uf}
            onChange={(e) => onChange('uf', e.target.value)}
            options={ufsOptions}
            required
            error={errors.uf}
          />

          <div className='md:col-span-2'>
            <InputField
              label='Município'
              name='municipio'
              value={data.municipio}
              onChange={(e) => onChange('municipio', e.target.value)}
              required
              error={errors.municipio}
              placeholder='Nome do município'
            />
          </div>
        </div>
      </div>

      {/* Vínculo Específico */}
      <div className='pt-6 mt-6'>
        <InputField
          label='Vínculo Específico'
          name='vinculoEspecifico'
          value={data.vinculoEspecifico}
          onChange={(e) => onChange('vinculoEspecifico', e.target.value)}
          required
          error={errors.vinculoEspecifico}
          placeholder='Ex: Servidor Público, Autônomo, Estudante, etc.'
        />
      </div>
    </div>
  );
}
